<template>
  <app-panel
    class="monitoring-map"
  >
    <label
      slot="panel-header"
      class="monitoring-map__title"
    > {{ $t('monitoring.map.title') }}
    </label>
    <s-loader
      v-if="loading"
      slot="panel-body"
      size="lg"
      class="monitoring-map__loader"
    />
    <div
      v-else
      class="monitoring-map__panel-body"
      slot="panel-body"
    >
      <edit-marker
        v-show="showEditModal"
        class="monitoring-map__edit-marker"
        :installationName="installationToEdit.name"
        :marker-to-edit.sync="markerToEdit"
        :draggedTo="draggedTo"
        :search.sync="search"
        @saveLocation="saveLocation"
        @cancelLocation="cancelLocation"
        @setPlace="setPlace($event, false)"
      />
      <div class="monitoring-map__map-wrapper">
        <l-map
          v-if="showMap"
          ref="map"
          :zoom="zoom"
          :min-zoom="2"
          :center="center"
          :options="mapOptions"
          :max-bounds="maxBounds"
          @contextmenu="onClickMap"
          @update:center="centerUpdate"
        >
          <l-tile-layer
            :url="url"
            :attribution="attribution"
          />
          <l-marker
            ref="markers"
            v-for="(marker) in marksHandler"
            :key="marker.information.id"
            :visible="marker.show"
            :draggable="marker.draggable"
            :lat-lng="marker.position"
            @click="toggleInfoWindow(marker)"
            @dragend="onUpdateLatLng($event, false)"
          >
            <l-icon
              :icon-size="[32, 37]"
              :icon-url="marker.information.icon"
            />
            <l-popup :options="popupOptions">
              <div class="monitoring-map__tooltip-info">
                <div>
                  <label
                  > {{ $t('monitoring.map.installation') }}:
                  </label>
                  {{infoContent.name}}
                </div>
                <div>
                  <label
                  >{{ $t('monitoring.map.has-alarms-acted') }}:
                  </label>
                  {{ hasAlarmsLabel }}
                </div>
                <div>
                  <label
                  >{{ $t('monitoring.map.total-alarms-acted') }}:
                  </label>
                  {{infoContent.totalAlarms}}
                </div>
              </div>
              <div class="monitoring-map__tooltip-buttons">
                <b-button
                  v-show="$hasUserPermission('PER008')"
                  variant="link"
                  class="monitoring-map__link text-blue"
                  @click="editLocation(infoContent.id)"
                > {{ $t('monitoring.map.edit-coordinates') }}
                </b-button>
              </div>
            </l-popup>
          </l-marker>
        </l-map>
      </div>
    </div>
  </app-panel>
</template>

<script>
import {
 LMap, LTileLayer, LMarker, LPopup, LIcon,
} from 'vue2-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapService from '@/domain/services/generalPanel/monitoring/map';
import EditMarker from './EditMarkerModal';

// eslint-disable-next-line no-underscore-dangle
delete L.Icon.Default.prototype._getIconUrl;

export default {
  name: 'MonitoringMap',
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    EditMarker,
    LTileLayer,
    LMap,
    LMarker,
    LPopup,
    LIcon,
  },
  data: () => ({
    loading: true,
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    currentCenter: 0,
    currentMidx: null,
    infoContent: {},
    markers: [],
    markerToEdit: {},
    draggedTo: {},
    search: '',
    showEditModal: false,
    showMap: true,
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    zoom: 7,
    mapOptions: {
      zoomSnap: 0.5,
    },
    installationToEdit: {
      id: null,
      lat: null,
      lng: null,
    },
    popupOptions: {
      offset: [0, -10],
      closeButton: false,
      keepInView: true,
      className: 'monitoring-map__popup',
    },
    center: {
      lat: 0,
      lng: 0,
    },
  }),
  methods: {
    onUpdateLatLng(ev, cleanSearch) {
      if (!this.showEditModal) return;
      // eslint-disable-next-line no-underscore-dangle
      this.draggedTo = ev.target._latlng;
      const event = {
        description: this.installationToEdit.name,
        // eslint-disable-next-line no-underscore-dangle
        latLng: ev.target._latlng,
      };
      this.setPlace(event, cleanSearch);
    },
    onClickMap(ev) {
      if (this.showEditModal) {
        this.draggedTo = ev.latlng;
        const event = {
          description: this.installationToEdit.name,
          latLng: ev.latlng,
        };
        this.setPlace(event, true);
      }
      if (ev.preventDefault) {
        ev.preventDefault();
      }
    },
    centerUpdate(center) {
      this.currentCenter = center;
    },
    toggleInfoWindow(marker) {
      if (this.showEditModal) return;
      this.infoContent = marker.information;
      this.center = marker.position;
    },
    closeMarkerPopups() {
      this.$refs.map.mapObject.closePopup();
    },
    editLocation(idInstallation) {
      const markerToEdit = this.markers.find(x => x.information.id === idInstallation);
      this.$confirmMessage({
        code: 'MC007',
        messageParams: {
          NomeInstalacao: markerToEdit.information.name,
        },
      }).accepted(() => {
        this.closeMarkerPopups();
        this.enterEditMode();
        this.hideMarkers(idInstallation);
        this.installationToEdit = {
          id: markerToEdit.information.id,
          lat: markerToEdit.position.lat,
          lng: markerToEdit.position.lng,
          name: markerToEdit.information.name,
        };
        markerToEdit.draggable = true;
        this.markerToEdit = markerToEdit;
      });
    },
    hideMarkers(idMarkerVisible) {
      const markersToHide = this.markers.filter(x => x.information.id !== idMarkerVisible);
      markersToHide.forEach((markerToHide, index) => {
        markersToHide[index].show = false;
      });
    },
    showMarkers() {
      this.markers.forEach((markerToShow, index) => {
        this.markers[index].show = true;
      });
    },
    setPlace(event, cleanSearch = true) {
      const markerToEdit = this.getMarkerToEdit();
      markerToEdit.position = {
        lat: event.latLng.lat,
        lng: event.latLng.lng,
      };
      this.center = markerToEdit.position;
      this.markerToEdit = markerToEdit;
      if (cleanSearch) {
        this.search = '';
      }
    },
    cancelLocation() {
      this.exitEditMode();
      const markerToEdit = this.getMarkerToEdit();
      markerToEdit.draggable = false;
      markerToEdit.position = {
        lat: this.installationToEdit.lat,
        lng: this.installationToEdit.lng,
      };
      this.center = markerToEdit.position;
      this.showMarkers();
    },
    saveInstallationPosition(markerToEdit) {
      return MapService.saveInstallationPosition({
        id: markerToEdit.information.id,
        latitude: markerToEdit.position.lat.toString(),
        longitude: markerToEdit.position.lng.toString(),
      });
    },
    getMarkerToEdit() {
      return this.markers
        .find(mark => mark.information.id === this.installationToEdit.id);
    },
    saveLocationAccepted(event) {
      const markerToEdit = this.getMarkerToEdit();
      markerToEdit.draggable = false;
      if (event.latLng && event.latLng.lat && event.latLng.lng) {
        markerToEdit.position = {
          lat: event.latLng.lat,
          lng: event.latLng.lng,
        };
      }
      this.center = markerToEdit.position;
      this.showMarkers();
      this.saveInstallationPosition(markerToEdit)
        .then(() => {
          this.markerToEdit = markerToEdit;
        })
        .catch(() => {
          this.cancelLocation();
        });
    },
    saveLocation(event) {
      const markerToEdit = this.getMarkerToEdit();
      this.exitEditMode();
      this.$confirmMessage({
        code: 'MC007',
        messageParams: {
          NomeInstalacao: markerToEdit.information.name,
        },
      }).accepted(() => {
        this.saveLocationAccepted(event);
      }).rejected(() => {
        setTimeout(() => {
          this.enterEditMode();
        }, 300);
      });
    },
    getMarkerStatusImage(alarms) {
      return alarms > 0 ? '/static/imgs/red-marker.png' : '/static/imgs/green-marker.png';
    },
    getInstallations() {
      this.loading = true;

      return MapService.getInstallations().then((installations) => {
        this.markers = installations;
        this.loading = false;
      });
    },
    exitEditMode() {
      this.showEditModal = false;
      this.markerToEdit = {};
      this.$emit('restartRefresh');
    },
    enterEditMode() {
      this.showEditModal = true;
      this.$emit('stopRefresh');
    },
    fitMapBounds() {
      const map = this.$refs.map || { fitBounds: () => ({}) };
      const markers = this.$refs.markers || [];
      const bounds = new L.LatLngBounds();
      markers.forEach(marker => bounds.extend(marker.latLng));
      map.fitBounds(bounds.pad(0.5));
    },
  },
  computed: {
    filteredMarkers() {
      if (!this.filters.installationsIds?.length > 0) {
        return this.markers;
      }

      return this.markers.filter(
        marker => this.filters.installationsIds.some(
          id => id.toLowerCase() === marker.information.id.toLowerCase(),
        ),
      );
    },
    maxBounds() {
      const firstCorner = L.latLng(-90, -180);
      const secondCorner = L.latLng(90, 180);
      return L.latLngBounds(firstCorner, secondCorner);
    },
    hasAlarmsLabel() {
      return this.infoContent.totalAlarms > 0
        ? this.$t('general.yes')
        : this.$t('general.no');
    },
    marksHandler() {
      if (!this.filteredMarkers.length > 0) {
        return [];
      }

      return this.filteredMarkers.map(marks => ({
        ...marks,
        position: {
          lat: Number.parseFloat(marks.position.lat),
          lng: Number.parseFloat(marks.position.lng),
        },
        information: {
          ...marks.information,
          icon: this.getMarkerStatusImage(marks.information.totalAlarms),
        },
      }));
    },
  },
  async created() {
    await this.getInstallations();
    this.fitMapBounds();
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .monitoring-map
    flex: 1
    &__panel-body
      flex: 1
      display: flex
      flex-direction: column
      height: 85vh
      position: relative
    &__edit-marker
      position: absolute
      top: 0
    &__map-wrapper
      width: 100%
      height: 85vh
      z-index: 1
    &__map
      flex: 1
    &__tooltip-info
      color: var(--color-gray-medium-dark)
      min-width: 220px
    &__tooltip-buttons
      display: flex
      justify-content: space-between
    &__link
      padding: 0
      text-transform: uppercase
      font-size: .85rem
    &__loader
      flex: 1
      display: flex
      flex-direction: column
      height: 85vh
      position: relative
    ::v-deep &__popup .leaflet-popup-content-wrapper
      border-radius: 2px
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>
